import { Box } from "@mui/system"
import { IOperationFilter } from "../../../../Store/Operations/types"
import { ISubjectsFilter } from "../../../../Store/Subjects/types"
import ChipFilter from "./ChipFilter"
import { useEffect, useState } from "react"
import { Button } from "@mui/material"
import { IAccountFilter } from "../../../../Store/Accounts/types"
import { IExceptionFilter } from "../../../../Store/Exceptions/types"
import { IAdjudicationPendingFilter, IAdjudicationSolvedFilter, IAdjudicatorDecisionFilter } from "../../../../Store/Adjudication/types"

type FilterUnion =
  | ISubjectsFilter
  | IOperationFilter
  | IAccountFilter
  | IExceptionFilter
  | IAdjudicationPendingFilter
  | IAdjudicatorDecisionFilter
  | IAdjudicationSolvedFilter

interface IChipFactoryProps<T extends FilterUnion> {
    clearFilter: () => void
    filter: T
    setChip: (filter: T) => void
    ignoreModality?: boolean
    style?: React.CSSProperties | undefined
}

const ChipFactory = <T extends FilterUnion>(props: IChipFactoryProps<T>): React.ReactElement => {
    const [clearAllButton, setClearAllButton] = useState(false)

    useEffect(() => {
        let allEmpty = false
        Object.keys(props.filter).forEach((f: string) => {
            if (props.filter[f as keyof typeof props.filter] !== undefined && (props.filter[f as keyof typeof props.filter] as unknown as Array<number>).length > 0) {
                if (
                    f !== "subjectsFieldToOrderBy" &&
                    f !== "fieldsOrder" &&
                    f !== "operationsFieldToOrderBy" &&
                    f !== "subjectId" &&
                    f !== "accountFieldToOrderBy" &&
                    f !== "modalities"
                ) {
                    allEmpty = true
                }  
            }
        })
        setClearAllButton(allEmpty)
    }, [props])

  return (
    <Box style={props.style ?? { paddingTop: "1rem" }}>
        {'operationTypes' in props.filter && (
                <ChipFilter
                    title='Operation type'
                    enumValue={props.filter.operationTypes}
                    type={"enum"}
                    onClick={() => props.setChip({...props.filter, operationTypes: undefined})}
                />
            )}
            {'livenessModes' in props.filter && (
                <ChipFilter
                    title='Liveness mode'
                    enumValue={props.filter.livenessModes}
                    type={"enum"}
                    onClick={() => props.setChip({ ...props.filter, livenessModes: undefined })}
                />
            )}
            {'statuses' in props.filter && (
                <ChipFilter
                    title='Statuses'
                    enumValue={props.filter.statuses}
                    type={"enum"}
                    onClick={() => props.setChip({ ...props.filter, statuses: undefined })}
                />
            )}
            {'icaoWarnings' in props.filter && (
                <ChipFilter
                    title='Icao warnings'
                    enumValue={props.filter.icaoWarnings}
                    type={"enum"}
                    onClick={() => props.setChip({ ...props.filter, icaoWarnings: undefined })}
                />
            )}
            {'dateLowerBound' in props.filter && 'dateUpperBound' in props.filter && (
                <ChipFilter
                    title='Date'
                    gteValue={props.filter.dateLowerBound}
                    lteValue={props.filter.dateUpperBound}
                    minValue={0}
                    maxValue={Math.floor(new Date().getTime() / 1000)}
                    type={"date"}
                    onClick={() =>
                        props.setChip({
                            ...props.filter,
                            dateLowerBound: undefined,
                            dateUpperBound: undefined
                        })
                    }
                />
            )}
            {'qualityLowerBound' in props.filter && 'qualityUpperBound' in props.filter && (
                <ChipFilter
                    title='Quality'
                    gteValue={props.filter.qualityLowerBound}
                    lteValue={props.filter.qualityUpperBound}
                    minValue={0}
                    maxValue={255}
                    type={"number"}
                    onClick={() =>
                        props.setChip({
                            ...props.filter,
                            qualityLowerBound: undefined,
                            qualityUpperBound: undefined
                        })
                    }
                />
            )}
            {'livenessScoreLowerBound' in props.filter && 'livenessScoreUpperBound' in props.filter && (
                <ChipFilter
                    title='Liveness score'
                    gteValue={props.filter.livenessScoreLowerBound}
                    lteValue={props.filter.livenessScoreUpperBound}
                    minValue={0}
                    maxValue={255}
                    type={"number"}
                    onClick={() =>
                        props.setChip({
                            ...props.filter,
                            livenessScoreLowerBound: undefined,
                            livenessScoreUpperBound: undefined
                        })
                    }
                />
            )}
            {'ageLowerBound' in props.filter && 'ageUpperBound' in props.filter && (
                <ChipFilter
                    title='Age'
                    gteValue={props.filter.ageLowerBound}
                    lteValue={props.filter.ageUpperBound}
                    minValue={0}
                    maxValue={255}
                    type={"number"}
                    onClick={() =>
                        props.setChip({
                            ...props.filter,
                            ageLowerBound: undefined,
                            ageUpperBound: undefined
                        })
                    }
                />
            )}
            {'modalities' in props.filter && !props.ignoreModality && (
                <ChipFilter
                    title='Modality'
                    enumValue={props.filter.modalities}
                    type={"enum"}
                    onClick={() => props.setChip({ ...props.filter, modalities: undefined })}
                />
            )}
            {'yawLowerBound' in props.filter && 'yawUpperBound' in props.filter && (
                <ChipFilter
                    title='Yaw'
                    gteValue={props.filter.yawLowerBound}
                    lteValue={props.filter.yawUpperBound}
                    minValue={0}
                    maxValue={255}
                    type={"number"}
                    onClick={() =>
                        props.setChip({
                            ...props.filter,
                            yawLowerBound: undefined,
                            yawUpperBound: undefined
                        })
                    }
                />
            )}
            {'rollLowerBound' in props.filter && 'rollUpperBound' in props.filter && (
                <ChipFilter
                    title='Roll'
                    gteValue={props.filter.rollLowerBound}
                    lteValue={props.filter.rollUpperBound}
                    minValue={0}
                    maxValue={255}
                    type={"number"}
                    onClick={() =>
                        props.setChip({
                            ...props.filter,
                            rollLowerBound: undefined,
                            rollUpperBound: undefined
                        })
                    }
                />
            )}
            {'pitchLowerBound' in props.filter && 'pitchUpperBound' in props.filter && (
                <ChipFilter
                    title='Pitch'
                    gteValue={props.filter.pitchLowerBound}
                    lteValue={props.filter.pitchUpperBound}
                    minValue={0}
                    maxValue={255}
                    type={"number"}
                    onClick={() =>
                        props.setChip({
                            ...props.filter,
                            pitchLowerBound: undefined,
                            pitchUpperBound: undefined
                        })
                    }
                />
            )}
            {'pitchLowerBound' in props.filter && 'pitchUpperBound' in props.filter && (
                <ChipFilter
                    title='Pitch'
                    gteValue={props.filter.pitchLowerBound}
                    lteValue={props.filter.pitchUpperBound}
                    minValue={0}
                    maxValue={255}
                    type={"number"}
                    onClick={() =>
                        props.setChip({
                            ...props.filter,
                            pitchLowerBound: undefined,
                            pitchUpperBound: undefined
                        })
                    }
                />
            )}
            {'uuid' in props.filter && (
                <ChipFilter
                    title='UUID'
                    str={props.filter.uuid}
                    type={"string"}
                    onClick={() => props.setChip({ ...props.filter, uuid: undefined })}
                />
            )}
            {'operationUuid' in props.filter && (
                <ChipFilter
                    title='Operation UUID'
                    str={props.filter.operationUuid}
                    type={"string"}
                    onClick={() => props.setChip({ ...props.filter, operationUuid: undefined })}
                />
            )}
            {'issues' in props.filter && (
                <ChipFilter
                    title='Issues'
                    prefix="ISSUE_"
                    enumValue={props.filter.issues}
                    type={"enum"}
                    onClick={() => props.setChip({ ...props.filter, issues: undefined })}
                />
            )}
            {'decision' in props.filter && (
                <ChipFilter
                    title='Decisions'
                    prefix="DECISION_"
                    enumValue={props.filter.decision}
                    type={"enum"}
                    onClick={() => props.setChip({ ...props.filter, decision: undefined })}
                />
            )}
            {'attack' in props.filter && (
                <ChipFilter
                    title='Attack'
                    prefix="ATTACK_"
                    enumValue={props.filter.attack}
                    type={"enum"}
                    onClick={() => props.setChip({ ...props.filter, attack: undefined })}
                />
            )}
            {'dateDecisionLowerBound' in props.filter && 'dateDecisionUpperBound' in props.filter && (
                <ChipFilter
                    title='Date'
                    gteValue={props.filter.dateDecisionLowerBound}
                    lteValue={props.filter.dateDecisionUpperBound}
                    minValue={0}
                    maxValue={Math.floor(new Date().getTime() / 1000)}
                    type={"date"}
                    onClick={() =>
                        props.setChip({
                            ...props.filter,
                            dateDecisionLowerBound: undefined,
                            dateDecisionUpperBound: undefined
                        })
                    }
                />
            )}
            {'dateAdjudicationLowerBound' in props.filter && 'dateAdjudicationUpperBound' in props.filter && (
                <ChipFilter
                    title='Date'
                    gteValue={props.filter.dateAdjudicationLowerBound}
                    lteValue={props.filter.dateAdjudicationUpperBound}
                    minValue={0}
                    maxValue={Math.floor(new Date().getTime() / 1000)}
                    type={"date"}
                    onClick={() =>
                        props.setChip({
                            ...props.filter,
                            dateAdjudicationLowerBound: undefined,
                            dateAdjudicationUpperBound: undefined
                        })
                    }
                />
            )}
            

      {clearAllButton ? (
        <Button variant="text" color="primary" onClick={props.clearFilter}>
          Clear all
        </Button>
      ) : null}
    </Box>
  )
}

export default ChipFactory